import React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Fade from '@mui/material/Fade';
import Text from '../text';
import AdminSettingsDialog from '../admin-settings';
import { useMsal } from '@azure/msal-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import { useSelector } from 'react-redux';
import { IReduxState } from 'utils/models/redux-states';
import { OPEN_SITES_AND_GROUPS_EVENT } from 'utils/constants/starterSite';

interface UserDropdownProps {
	anchorEl: null | HTMLElement;
	onClose: () => void;
	userName: string;
	user?: any;
	isConnected: boolean;
	isAnalyzerConnected: boolean;
	handleConnectToPort: () => void;
}

const UserDropdown: React.FC<UserDropdownProps> = ({
	anchorEl,
	onClose,
	userName,
	user,
	isConnected,
	isAnalyzerConnected,
	handleConnectToPort
}) => {
	const [openDialog, setOpenDialog] = React.useState(false);
	const [activeAdminTab, setActiveAdminTab] = React.useState(0);
	const { instance } = useMsal();
	const { accounts } = useMsal();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const reduxState = useSelector((state: IReduxState) => state.user);
	const userData = reduxState?.userInformation;
	const NAV_LABEL_MAP: [string[], string][] = [
		[["Bugcount® Analyzer", "Bugcount Analyzer Warranty Registration"], "Analyzer Warranty"],
		[["MANAGE SITES", "Sites & Groups"], "Site & Group Management"],
		[["LAB SERVICE REQUESTS", "COC"], "Lab Service Requests"],
		[["MY ACCOUNT", "My Account"], "My Account"],
	];
	function mapNavLabel(value: string): string | undefined {
		const match = NAV_LABEL_MAP.find(([aliases]) => aliases.includes(value));
		return match?.[1];
	}
	const handleMenuItemClick = (setting: any) => {
		if (setting.name === 'Log Out') {
			localStorage.removeItem('loginRedirectedFrom');
			localStorage.removeItem('auth_token');
			instance.logoutRedirect({
				account: accounts[0],
				postLogoutRedirectUri: '/',
			});
			addEventLog("Sign Out", "Session Management", undefined, undefined, `User: ${accounts[0]?.username}`);
			return onClose();
		}
		const newVal = mapNavLabel(setting.name);
		addEventLog(`${newVal} Opened`, "Administration", undefined, undefined);
		if(setting.name === 'Reports (BETA)' && navigator.onLine){
			navigate('/reports');
		}
		setActiveAdminTab(setting.id);
		setOpenDialog(true);
		return onClose();
	};

	// Site cards can't reach this dialog, so they ask for Sites & Groups with an event (starter site, PWA-3011).
	React.useEffect(() => {
		const openSitesAndGroups = () => handleMenuItemClick({ id: 0, name: 'Sites & Groups' });
		window.addEventListener(OPEN_SITES_AND_GROUPS_EVENT, openSitesAndGroups);
		return () => window.removeEventListener(OPEN_SITES_AND_GROUPS_EVENT, openSitesAndGroups);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<Menu
				id="main-menu-dropdown"
				sx={{
					'& .MuiPaper-root': {
						width: '210px',
						maxWidth: '210px',
						minWidth: '210px',
						top:'6px !important',
						left:'unset !important',
						right:0,
						borderRadius:"0px !important",
						borderBottomLeftRadius: '2px !important',
						borderTopLeftRadius: '2px !important',
					},
					'& .MuiList-root': {
						padding: '0px !important',
					},
					height: 'auto',
					mt: '45px',
					paddingTop: '0px',
					
					'& .MuiMenuItem-root': {
						fontFamily: 'inter, sans-serif !important',
						padding: '6px 0px',
						display: 'flex',
						alignItems: 'center',
						'&:hover': {
							backgroundColor: '#F4FCFF',
						},
					},
				}}
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				keepMounted
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				open={Boolean(anchorEl)}
				onClose={onClose}

				TransitionComponent={Fade}  // Change the animation here
  				transitionDuration={200}  // Adjust animation speed
			>
				<div style={{padding:"4px", backgroundColor:"#556670"}}>
				<Text addStyle="!text-[12px] !font-jost !text-white !text-center">
					WORKFLOWS
				</Text>
				</div>

				{/* ATP Testing Tab */}
				<MenuItem
					onClick={() => {
						if(pathname !== '/')
							addEventLog('ATP Testing Opened', 'Relay Menu Change'); 
						navigate('/');}}
					style={{
						height: '52px', flex: '0 0 auto', boxShadow: "rgba(0, 0, 0, 0.4) 0px 7px 3px -6px inset",
						background: !window.location.href.includes("reports") ? '#F4FCFF' : ''
					}}
					sx={{
						'&:hover': {
							backgroundColor: '#000',
						},
					}}
				>
					<div className="flex items-center justify-center w-full">
						<Text addStyle="!text-[16px] !font-inter !font-semibold !text-primary-black !text-center">
							ATP Testing
						</Text>
					</div>
				</MenuItem>

				{/* Reports (BETA) Tab */}
				<MenuItem
					onClick={() => {
						if(pathname !== '/reports')
							addEventLog('Lab Reports Opened', 'Relay Menu Change');
						navigate('/reports')
					}}
					style={{ 
						height: '52px', borderTop: '1px solid #E5E7EB', flex: '0 0 auto',
						background: window.location.href.includes("reports") ? '#F4FCFF' : ''
					}}
					disabled={!navigator.onLine}
					sx={{
						'&:hover': {
							backgroundColor: '#000',
						},
					}}
				>
					<div className="flex items-center justify-center w-full">
						<Text addStyle="!text-[16px] !font-inter !font-semibold !text-primary-black !text-center">
							Lab Reports
						</Text>
					</div>
				</MenuItem>

				<div style={{padding:"4px", backgroundColor:"#556670", boxShadow:"0px 4px 5px -5px inset rgba(0,0,0,0.3)"}}>
					<Text addStyle="!text-[12px] !font-jost !text-white !text-center">
						ADMIN
					</Text>
					</div>

				{/* Sites & Groups Tab */}
				<MenuItem
					onClick={() => handleMenuItemClick({ id: 0, name: 'Sites & Groups' })}
					style={{
					height: '', padding:"10px 0 10px",  flex:'0 0 auto', marginTop:'auto',
					boxShadow:"rgba(0, 0, 0, 0.4) 0px 7px 3px -6px inset",
					}}
					sx={{
					'&:hover': {
						backgroundColor: '#000', 
					},
					}}
				>
					<div className="flex items-center">
					<img
						src="/assets/icons/location3.svg"
						alt="Sites"
						className="ml-4 w-5 h-5"
					/>
					<Text addStyle="!text-[14px] !font-inter !text-primary-black !ml-3" style={{fontWeight:500}}>
						Manage Sites
					</Text>
					</div>
				</MenuItem>

				{/* Bugcount Warranty Tab */}
				<MenuItem
					onClick={() => handleMenuItemClick({ id: 1, name: 'Bugcount Analyzer Warranty Registration' })}
					style={{
					height: '',
					padding:"10px 0 10px",
					flex:'0 0 auto', 
					marginTop:'auto',
					borderTop: '1px solid #E5E7EB',
					width:'100%',
											}}
					sx={{
					'&:hover': {
						backgroundColor: '#000',
					},
					}}
					disabled={!navigator.onLine}
				>
					<div className="flex items-center my-account">
					<img
						src="/assets/icons/analyzer_icon.png"
						alt="Account"
						className="ml-4 w-5 h-5"
					/>
					<Text addStyle="!text-[14px] !font-inter !text-primary-black !ml-3" style={{fontWeight:500}}>
						Bugcount® Analyzer
					</Text>

					</div>
				</MenuItem>
				{/* Lab Service Requests */}
				{userData?.organizationVO?.id !== 2 && <MenuItem
					onClick={() => handleMenuItemClick({ id: 2, name: 'COC' })}
					style={{
					height: '', padding:"10px 0 10px", borderTop: '1px solid #E5E7EB', flex:'0 0 auto', marginTop:'auto',
					// backgroundColor:"rgb(244, 248, 249)", 
					// boxShadow:"rgba(0, 0, 0, 0.4) 0px 5px 3px -6px inset"
					}}
					sx={{
					'&:hover': {
						backgroundColor: '#000',
					},
					}}
					disabled={!navigator.onLine}
				>
					<div className="flex items-center">
					<img
						src="/assets/icons/lab.svg"
						alt="Lab"
						className="ml-4 w-5 h-5"
					/>
					<Text addStyle="!text-[14px] !font-inter !text-primary-black !ml-3" style={{fontWeight:500}}>
						Lab Service Requests
					</Text>
					</div>
				</MenuItem>}
				{/* My Account Tab */}
				<MenuItem
					onClick={() => handleMenuItemClick({ id: userData?.organizationVO?.id === 2 ? 2 : 3, name: 'My Account' })}
					style={{
					height: '',
					padding:"10px 0 10px",
					flex:'0 0 auto', 
					marginTop:'auto',
					borderTop: '1px solid #E5E7EB',
					width:'100%',
											}}
					sx={{
					'&:hover': {
						backgroundColor: '#000',
					},
					}}
				>
					<div className="flex items-center my-account">
					<img
						src="/assets/icons/account3.svg"
						alt="Account"
						className="ml-4 w-5 h-5"
					/>
					<Text addStyle="!text-[14px] !font-inter !text-primary-black !ml-3" style={{fontWeight:500}}>
						My Account
					</Text>

					</div>
				</MenuItem>
				{/* Log Out Tab */}
				<MenuItem
					onClick={() => handleMenuItemClick({ id: 4, name: 'Log Out' })}
					style={{
					height: '',
					padding:"10px 0 10px",
					borderTop: '1px solid #E5E7EB',
					marginTop:'auto',
					width:'100%',
					flex:'0 0 auto' 
					}}
					sx={{
					'&:hover': {
						backgroundColor: '#000',
					},
					}}
				>
					<div className="flex items-center">
					<img
						src="/assets/icons/Log-out.svg"
						alt="Log Out Icon"
						className="ml-4 w-5 h-5"
					/>
					<Text addStyle="!text-[14px] !font-inter !text-primary-black !ml-3" style={{fontWeight:500}}>
						Log Out
					</Text>
					</div>
				</MenuItem>

			</Menu>
			<AdminSettingsDialog
				isOpen={openDialog}
				onClose={() => {
					setOpenDialog(false);
					addEventLog(`Administation Settings Closed`, "Administration", undefined, undefined);
				}}
				mode={'online'}
				user={user}
				setActiveAdminTab={setActiveAdminTab}
				activeAdminTab={activeAdminTab}
				handleConnectToPort={handleConnectToPort}
				isConnected={isConnected}
				isAnalyzerConnected={isAnalyzerConnected}
			/>
		</>
	);
};

export default UserDropdown;
